import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Home,
  Plus,
  Settings,
  Calendar,
  DollarSign,
  Wifi,
  MapPin,
  Trash2,
  Edit,
  Eye,
  MoreVertical,
  Building,
  Users,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const APARTMENT_TYPES = [
  'Apartamento Turístico',
  'Casa Vacacional',
  'Apartaestudio',
  'Villa',
  'Cabaña',
  'Loft',
  'Penthouse',
  'Otro'
];

export default function ApartamentosPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: 'Apartamento Turístico',
    direccion: '',
    ciudad: '',
    departamento: '',
    capacidad: 2,
    habitaciones: 1,
    banos: 1,
    precio_base: 0,
    descripcion: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Error al cargar apartamentos');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    try {
      const propertyData = {
        nombre: formData.nombre,
        rnt: `APT-${Date.now()}`,
        direccion: formData.direccion,
        ciudad: formData.ciudad,
        departamento: formData.departamento || formData.ciudad,
        pais: 'Venezuela',
        telefono: '0000000000',
        email: 'apto@example.com',
        tipo: formData.tipo,
        pms_type: 'apartamento',
        num_habitaciones: formData.habitaciones,
        descripcion: formData.descripcion
      };

      await axios.post(`${BACKEND_URL}/api/properties`, propertyData);
      toast.success('Apartamento agregado exitosamente');
      setShowAddModal(false);
      setFormData({
        nombre: '',
        tipo: 'Apartamento Turístico',
        direccion: '',
        ciudad: '',
        departamento: '',
        capacidad: 2,
        habitaciones: 1,
        banos: 1,
        precio_base: 0,
        descripcion: ''
      });
      fetchProperties();
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error(error.response?.data?.detail || 'Error al agregar apartamento');
    }
  };

  // Get occupancy status for a property
  const getOccupancyStatus = (property) => {
    // This would be calculated from reservations
    return { status: 'disponible', guest: null };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{fontFamily: 'Manrope'}}>
              Mis Apartamentos
            </h1>
            <p className="text-muted-foreground">
              Gestiona tus {properties.length} de 10 apartamentos permitidos
            </p>
          </div>
          <Button 
            onClick={() => setShowAddModal(true)}
            className="bg-slate-700 hover:bg-slate-600"
            disabled={properties.length >= 10}
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Apartamento
          </Button>
        </div>

        {/* Usage Bar */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Capacidad utilizada</span>
              <span className="text-sm text-muted-foreground">{properties.length}/10 apartamentos</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-slate-700 h-2 rounded-full transition-all"
                style={{ width: `${(properties.length / 10) * 100}%` }}
              ></div>
            </div>
          </CardContent>
        </Card>

        {/* Apartments Grid */}
        {properties.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Building className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No tienes apartamentos registrados</h3>
              <p className="text-muted-foreground mb-4">
                Agrega tu primer apartamento para comenzar a gestionar tus reservas
              </p>
              <Button 
                onClick={() => setShowAddModal(true)}
                className="bg-slate-700 hover:bg-slate-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar mi primer apartamento
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => {
              const occupancy = getOccupancyStatus(property);
              const isOccupied = occupancy.status === 'ocupado';
              
              return (
                <Card key={property.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  {/* Property Image/Header */}
                  <div className={`h-32 relative ${isOccupied ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-slate-600 to-slate-700'}`}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Home className="h-16 w-16 text-white/20" />
                    </div>
                    <div className="absolute top-3 right-3">
                      <Badge className={isOccupied ? 'bg-blue-700' : 'bg-slate-700'}>
                        {isOccupied ? 'Ocupado' : 'Disponible'}
                      </Badge>
                    </div>
                    <div className="absolute bottom-3 left-3">
                      <Badge variant="outline" className="bg-white/20 text-white border-white/30">
                        {property.codigo_unico || 'Sin código'}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardContent className="pt-4">
                    <h3 className="font-bold text-lg mb-1 truncate">{property.nombre}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mb-3">
                      <MapPin className="h-3 w-3" />
                      {property.ciudad}, {property.departamento || property.pais}
                    </p>
                    
                    <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                      <div className="bg-muted rounded-md p-2">
                        <p className="text-xs text-muted-foreground">Tipo</p>
                        <p className="text-sm font-medium truncate">{property.tipo}</p>
                      </div>
                      <div className="bg-muted rounded-md p-2">
                        <p className="text-xs text-muted-foreground">Hab.</p>
                        <p className="text-sm font-medium">{property.num_habitaciones}</p>
                      </div>
                      <div className="bg-muted rounded-md p-2">
                        <p className="text-xs text-muted-foreground">Estado</p>
                        <p className="text-sm font-medium capitalize">{property.estado_aprobacion}</p>
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Calendar className="h-4 w-4 mr-1" />
                        Reservas
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1">
                        <DollarSign className="h-4 w-4 mr-1" />
                        Tarifas
                      </Button>
                      <Button variant="outline" size="sm">
                        <Settings className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-sky-50 border-sky-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Home className="h-5 w-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="font-medium text-emerald-800">Gestión Independiente</p>
                <p className="text-sm text-emerald-700">
                  Cada apartamento tiene su propia configuración de tarifas, conexiones OTA y calendario.
                  Puedes ver todos tus apartamentos en el Calendario Unificado para una visión global de ocupación.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Property Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Apartamento</DialogTitle>
            <DialogDescription>
              Ingresa los datos de tu nuevo apartamento
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAddProperty} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del apartamento *</Label>
              <Input
                placeholder="Ej: Apartamento Vista Mar"
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo de propiedad *</Label>
              <Select 
                value={formData.tipo} 
                onValueChange={(v) => setFormData({...formData, tipo: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APARTMENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ciudad *</Label>
                <Input
                  placeholder="Caracas"
                  value={formData.ciudad}
                  onChange={(e) => setFormData({...formData, ciudad: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Estado/Departamento</Label>
                <Input
                  placeholder="Miranda"
                  value={formData.departamento}
                  onChange={(e) => setFormData({...formData, departamento: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                placeholder="Av. Principal, Edificio..."
                value={formData.direccion}
                onChange={(e) => setFormData({...formData, direccion: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Habitaciones</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.habitaciones}
                  onChange={(e) => setFormData({...formData, habitaciones: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Capacidad</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.capacidad}
                  onChange={(e) => setFormData({...formData, capacidad: parseInt(e.target.value)})}
                />
              </div>
              <div className="space-y-2">
                <Label>Baños</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.banos}
                  onChange={(e) => setFormData({...formData, banos: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-slate-700 hover:bg-slate-600">
                Agregar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
